import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Search, Filter, ChevronRight, MapPin, DollarSign } from "lucide-react";

const EMP_TYPES = ["full-time", "part-time", "temporary", "contract", "seasonal"];
const ARRANGE = ["on-site", "hybrid", "remote"];

export default function JobsPage() {
  const [items, setItems] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [industry, setIndustry] = useState(params.get("industry") || "");
  const [emp, setEmp] = useState(params.get("employment_type") || "");
  const [arr, setArr] = useState(params.get("work_arrangement") || "");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs", {
        params: {
          q: q || undefined,
          industry: industry || undefined,
          employment_type: emp || undefined,
          work_arrangement: arr || undefined,
        },
      });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [industry, emp, arr]);
  useEffect(() => { api.get("/admin/industries").then(r => setIndustries(r.data.items || [])).catch(() => {}); }, []);

  const search = (e) => {
    e.preventDefault();
    setParams({ q, industry, employment_type: emp, work_arrangement: arr });
    load();
  };

  return (
    <div className="section container-ax">
      <div className="eyebrow mb-2">Job Search</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold text-navy">Find your next opportunity.</h1>

      <form onSubmit={search} className="mt-8 grid md:grid-cols-12 gap-3 items-end bg-white border border-slate-200 rounded-xl p-4">
        <div className="md:col-span-5">
          <label className="text-xs text-slate-500">Keyword</label>
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)}
              data-testid={TID.jobSearchInput}
              placeholder="Title, skill, company"
              className="pl-9 w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm" />
          </div>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs text-slate-500">Industry</label>
          <select value={industry} onChange={e => setIndustry(e.target.value)}
            data-testid={TID.jobFilterIndustry}
            className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm">
            <option value="">All industries</option>
            {industries.map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-slate-500">Type</label>
          <select value={emp} onChange={e => setEmp(e.target.value)}
            className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm">
            <option value="">Any</option>
            {EMP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-slate-500">Setup</label>
          <select value={arr} onChange={e => setArr(e.target.value)}
            className="w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm">
            <option value="">Any</option>
            {ARRANGE.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="md:col-span-12">
          <button type="submit" className="btn-navy">
            <Filter className="w-4 h-4" /> Search
          </button>
        </div>
      </form>

      <div className="mt-8 text-sm text-slate-500">{loading ? "Loading…" : `${items.length} job${items.length === 1 ? "" : "s"} found`}</div>

      <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(j => (
          <Link key={j.id} to={`/jobs/${j.id}`} data-testid={TID.jobCard(j.id)}
            className="card-lift bg-white border border-slate-200 rounded-xl p-5 block">
            <div className="text-xs uppercase tracking-widest text-slate-500">{j.industry}</div>
            <h3 className="font-display text-lg font-semibold text-navy mt-1">{j.title}</h3>
            <div className="text-sm text-slate-600 mt-1">{j.employer_name}</div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {j.employment_type && <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{j.employment_type}</span>}
              {j.work_arrangement && <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{j.work_arrangement}</span>}
            </div>
            <div className="mt-4 flex flex-col gap-1 text-sm text-slate-600">
              {j.city && <div className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.city}{j.state ? `, ${j.state}` : ""}</div>}
              {(j.salary_min || j.salary_max) && (
                <div className="inline-flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  {j.salary_min || "—"}{j.salary_max ? `–${j.salary_max}` : ""} / {j.pay_type}
                </div>
              )}
            </div>
            <div className="mt-4 text-sm font-medium text-navy inline-flex items-center gap-1">
              View role <ChevronRight className="w-4 h-4" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
